import { formatStudyTime } from 'utils/studyTime';
import type { BulletinSubjectCategory } from 'api/v1/requests/bulletins';

import { locationMap, valueMap } from 'modules/Details/NewTask/comfig';

export { locationMap, valueMap };

const SLUG_RU: Record<string, string> = {
  algebra: 'Алгебра',
  geometriya: 'Геометрия',
  matematika: 'Математика',
  podgotovka_k_oge: 'Подготовка к ОГЭ',
  podgotovka_k_ege: 'Подготовка к ЕГЭ',
  podgotovka_k_ege_baza: 'Подготовка к ЕГЭ: базовый уровень',
  podgotovka_k_ege_profilnyj: 'Подготовка к ЕГЭ: профильный уровень',
  povyshenie_uspevaemosti: 'Повышение успеваемости',
  nachalnaya_shkola: 'Начальная школа',
  angliyskiy_yazyk: 'Английский язык',
};

const looksLikeSlug = (value?: string) =>
  !!value && /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/i.test(value);

const labelFromSlug = (value?: string) => {
  if (!value?.trim()) return '';
  const key = value.trim().toLowerCase().replace(/-/g, '_');
  if (SLUG_RU[key]) return SLUG_RU[key];
  if (!looksLikeSlug(value.trim())) return value.trim();
  return '';
};

const displayRu = (value?: string | null, slugFallback?: string | null): string => {
  const raw = value?.trim() || '';
  if (raw && /[а-яе]/i.test(raw)) return raw;
  if (raw && !looksLikeSlug(raw)) return raw;
  return labelFromSlug(raw || slugFallback || '');
};

export function formatSubjectSlug(slug?: string | null): string {
  return displayRu(slug, slug);
}

const RESPONSE_STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает ответа',
  accepted: 'Принят',
  rejected: 'Отклонен',
  withdrawn: 'Отозван',
  declined: 'Отклонен',
  cancelled: 'Отменен',
};

/** Заголовок заявки: подкатегория (без дублирования категории). */
export function formatBulletinSubjectTitle(
  subject?: BulletinSubjectCategory | null,
): string {
  if (!subject) return 'Заявка';
  const sub = displayRu(subject.subcategory_ru, subject.subcategory);
  if (sub) return sub;
  const cat = displayRu(subject.category_ru, subject.category);
  if (cat) return cat;
  return 'Заявка';
}

export function formatBulletinCategoryLine(
  subject?: BulletinSubjectCategory | null,
): string {
  if (!subject) return '';
  const cat = displayRu(subject.category_ru, subject.category);
  const sub = displayRu(subject.subcategory_ru, subject.subcategory);
  if (cat && sub) return `${cat} - ${sub}`;
  return cat || sub || '';
}

export function formatBulletinGrades(bulletin: {
  grade?: number | null;
  grades?: number[] | null;
}): string {
  if (bulletin.grades?.length) {
    return bulletin.grades.map((g) => `${g} класс`).join(', ');
  }
  if (bulletin.grade != null) {
    return `${bulletin.grade} класс`;
  }
  return '';
}

export function formatBulletinPrice(
  priceFrom?: number | null,
  priceTo?: number | null,
): string {
  const fromStr = priceFrom
    ? `от ${new Intl.NumberFormat('ru-RU').format(priceFrom)}`
    : '';
  const toStr = priceTo ? `до ${new Intl.NumberFormat('ru-RU').format(priceTo)}` : '';
  if (!fromStr && !toStr) return 'Цена не указана';
  return `${fromStr} ${toStr} руб./час`.trim();
}

export function formatBulletinStudyMode(selfStudy?: boolean | null): string {
  if (selfStudy === false) return 'Для ребенка';
  if (selfStudy === true) return 'Ученик сам';
  return '';
}

/** Краткое описание заявки для списка (2-4 строки). */
export function formatBulletinPreviewLines(bulletin: {
  student_type?: string | null;
  grade?: number | null;
  grades?: number[] | null;
  student_name?: string | null;
  planned_duration?: string | null;
  study_locations?: string[] | null;
  study_schedule?: string[] | null;
  study_time?: { mode?: string; days?: string[]; time_from?: string | null; time_to?: string | null; slots?: Array<{ day: string; time_from: string; time_to: string }> } | null;
  tutor_gender_preference?: string | null;
  trial_lesson_preference?: string | null;
  wishes?: string | null;
  self_study?: boolean;
}): string[] {
  const lines: string[] = [];
  const audience = translateStudentType(bulletin.student_type);
  const grades = formatBulletinGrades(bulletin);
  const student = bulletin.student_name?.trim();
  const who = [audience, grades, student].filter(Boolean).join(' - ');
  if (who) lines.push(who);

  const format = formatBulletinStudyMode(bulletin.self_study);
  const duration = translatePlannedDuration(bulletin.planned_duration);
  const place = translateStudyLocations(bulletin.study_locations);
  const meta = [format, duration, place].filter(Boolean).join(' - ');
  if (meta) lines.push(meta);

  const schedule = formatBulletinSchedule(bulletin);
  if (schedule) lines.push(`Удобное время: ${schedule}`);

  const tutorGender = translateTutorGender(bulletin.tutor_gender_preference);
  const trial = translateTrialPreference(bulletin.trial_lesson_preference);
  const prefs = [tutorGender ? `Репетитор: ${tutorGender}` : '', trial ? `Пробное: ${trial}` : '']
    .filter(Boolean)
    .join(' - ');
  if (prefs) lines.push(prefs);

  if (bulletin.wishes?.trim()) {
    lines.push(`Пожелания: ${bulletin.wishes.trim()}`);
  }
  return lines;
}

export function translateStudentType(value?: string | null): string {
  if (!value) return '';
  return valueMap.studentType?.[value] || value;
}

export function translateTutorGender(value?: string | null): string {
  if (!value) return '';
  return valueMap.teacherGender?.[value] || value;
}

export function translatePlannedDuration(value?: string | null): string {
  if (!value) return '';
  return valueMap.duration?.[value] || value;
}

export function translateTrialPreference(value?: string | null): string {
  if (!value) return '';
  return valueMap.trial?.[value] || value;
}

export function translateStudyLocation(value: string): string {
  return locationMap[value] || value;
}

const SCHEDULE_DAYS: Record<string, string> = { weekdays: 'будни', weekends: 'выходные' };
const SCHEDULE_TIMES: Record<string, string> = { morning: 'утро', afternoon: 'день', evening: 'вечер' };

/** «Когда удобно заниматься» из кодов заявки: «Будни, вечер» или «Гибко, обсудим с репетитором». */
export function formatStudySchedule(codes?: string[] | null): string {
  if (!codes?.length) return '';
  if (codes.includes('flexible')) return 'Гибко, обсудим с репетитором';
  const days = codes.map((c) => SCHEDULE_DAYS[c]).filter(Boolean);
  const times = codes.map((c) => SCHEDULE_TIMES[c]).filter(Boolean);
  const text = [days.join(' и '), times.join(', ')].filter(Boolean).join(', ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/** Удобное время заявки: структурное study_time, иначе старые коды study_schedule. */
export function formatBulletinSchedule(bulletin: {
  study_schedule?: string[] | null;
  study_time?: { mode?: string; days?: string[]; time_from?: string | null; time_to?: string | null; slots?: Array<{ day: string; time_from: string; time_to: string }> } | null;
}): string {
  const t = bulletin.study_time;
  if (t && (t.days?.length || t.time_from || t.time_to)) {
    // Весь объект: режим (daily/days/per_day) и слоты по дням влияют на текст.
    return formatStudyTime(t);
  }
  return formatStudySchedule(bulletin.study_schedule);
}

export function translateStudyLocations(locations?: string[] | null): string {
  if (!locations?.length) return '';
  return locations.map(translateStudyLocation).join(', ');
}

export function translateResponseStatus(status?: string | null): string {
  if (!status) return '';
  const key = status.toLowerCase();
  return RESPONSE_STATUS_LABELS[key] || status;
}

export function translateParticipantSex(sex?: string | number | null): string {
  if (sex === 1 || sex === '1' || sex === 'male') {
    return 'мужской';
  }
  if (sex === 0 || sex === '0' || sex === 'female') {
    return 'женский';
  }
  return ' - ';
}

export function participantAgeYears(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) {
    return null;
  }
  const birth = new Date(dateOfBirth.slice(0, 10));
  if (Number.isNaN(birth.getTime())) {
    return null;
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

export function formatParticipantLine(participant: {
  name?: string | null;
  surname?: string | null;
  patronymic?: string | null;
  sex?: string | number | null;
  date_of_birth?: string | null;
}): string {
  const name = [participant.surname, participant.name, participant.patronymic]
    .filter(Boolean)
    .join(' ')
    .trim();
  const sex = translateParticipantSex(participant.sex);
  const age = participantAgeYears(participant.date_of_birth);
  const ageLabel = age != null ? `${age} лет` : 'возраст не указан';
  return name ? `${name} - ${sex}, ${ageLabel}` : `${sex}, ${ageLabel}`;
}

export function translateTutorResponseLabel(label?: string | null): string {
  if (!label) return '';
  const map: Record<string, string> = {
    'не отправлено': 'Отклик не отправлен',
    отправлено: 'Отклик отправлен',
    отклонено: 'Отклик отклонен',
  };
  return map[label.toLowerCase()] || label;
}
